/**
 * Admin API: Yêu cầu rút tiền merchant
 * GET /api/v1/admin/withdrawal-requests
 */
import api from './api';

const BASE = '/admin/withdrawal-requests';

export type WithdrawalStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'PROCESSING'
  | 'REJECTED'
  | 'COMPLETED';

export interface WithdrawalRequestRow {
  id: number;
  public_code: string;
  restaurant_id: number;
  requested_by_user_id: number | null;
  balance_snapshot_vnd: number;
  requested_amount_vnd: number;
  withdrawal_fee_vnd: number;
  estimated_balance_after_vnd: number;
  bank_name: string | null;
  bank_account_number: string | null;
  bank_account_name: string | null;
  status: WithdrawalStatus;
  reviewed_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  restaurant_name: string | null;
  bank_code?: string | null;
  disbursement_provider?: string | null;
  appotapay_partner_ref_id?: string | null;
  appotapay_trans_id?: string | null;
  transfer_status?: string | null;
  transfer_error_message?: string | null;
  requested_by_email?: string | null;
}

export interface WithdrawalRequestDetail extends WithdrawalRequestRow {
  admin_note?: string | null;
  rejection_reason?: string | null;
  reviewed_by_admin_id?: number | null;
  reviewed_by_email?: string | null;
  requested_by_name?: string | null;
  requested_by_phone?: string | null;
}

export interface WalletLiveSummary {
  currentBalance: number;
  currentBalanceVnd: number;
  status: string;
}

export interface WithdrawLiveSummary {
  balanceVnd: number | null;
  source: string | null;
  errorCode?: string | null;
  provider?: string | null;
}

export interface PartnerLiquiditySummary {
  balanceVnd: number | null;
  sufficient: boolean | null;
  error?: string | null;
  requestedAmountVnd?: number;
}

export interface WithdrawalSubAccountSummary {
  merchantId?: number;
  referId?: string | null;
  /** Partner code REFER lưu trên hệ thống */
  partnerCode?: string | null;
  /** Partner code thực tế dùng khi sync gần nhất (merchant credentials) */
  usedPartnerCode?: string | null;
  hasCredentials?: boolean;
  status?: string | null;
  accountName?: string | null;
  appotapayBalanceVnd?: number | null;
  appotapayLastSyncedAt?: string | null;
  appotapaySyncError?: string | null;
  syncSource?: string | null;
  synced?: boolean;
  balanceSuccess?: boolean;
  balanceError?: string | null;
}

export interface WithdrawalReviewHint {
  code: string;
  message: string;
}

export interface WithdrawalReviewHints {
  canApprove: boolean;
  blockers: WithdrawalReviewHint[];
  warnings: WithdrawalReviewHint[];
  totalDebitVnd: number;
}

export interface WithdrawalAuditLog {
  id: number;
  action: string;
  result: string;
  errorCode?: string | null;
  errorMessage?: string | null;
  merchantAppotapayBalanceVnd?: number | null;
  requestedAmountVnd?: number | null;
  appotapayTransId?: string | null;
  adminNote?: string | null;
  ipAddress?: string | null;
  createdAt?: string | null;
}

export interface WithdrawalDetailResponse {
  request: WithdrawalRequestDetail;
  walletLive: WalletLiveSummary;
  /** Số dư có thể rút (sales_balance_vnd nội bộ) */
  withdrawableLive?: WithdrawLiveSummary;
  /** Tài khoản AppotaPay REFER của merchant (số dư sync từ API) */
  subAccount?: WithdrawalSubAccountSummary;
  /** Số dư Master (tham khảo, không dùng để chi hộ REFER) */
  partnerLiquidity?: PartnerLiquiditySummary;
  /** Gợi ý kiểm duyệt trước khi duyệt */
  reviewHints?: WithdrawalReviewHints;
  /** Lịch sử mỗi lần admin nhấn duyệt / từ chối / retry */
  auditLogs?: WithdrawalAuditLog[];
}

export interface WithdrawalListParams {
  page?: number;
  limit?: number;
  q?: string;
  keyword?: string;
  status?: WithdrawalStatus;
  restaurantId?: number;
  from?: string;
  to?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export async function fetchWithdrawalRequests(params: WithdrawalListParams) {
  const res = await api.get<{
    success: boolean;
    data?: WithdrawalRequestRow[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
    message?: string;
  }>(BASE, { params });
  return res.data;
}

export async function fetchWithdrawalRequestDetail(
  id: number,
  refresh = false
) {
  const res = await api.get<{
    success: boolean;
    data?: WithdrawalDetailResponse;
    message?: string;
  }>(`${BASE}/${id}`, {
    params: refresh ? { refresh: '1' } : undefined,
  });
  return res.data;
}

export async function approveWithdrawalRequest(
  id: number,
  body?: { adminNote?: string | null }
) {
  const res = await api.post(`${BASE}/${id}/approve`, body ?? {});
  return res.data;
}

export async function rejectWithdrawalRequest(
  id: number,
  body: { rejectionReason: string }
) {
  const res = await api.post(`${BASE}/${id}/reject`, body);
  return res.data;
}

export async function completeWithdrawalRequest(
  id: number,
  body?: { adminNote?: string | null }
) {
  const res = await api.post(`${BASE}/${id}/complete`, body ?? {});
  return res.data;
}
